import { Chips } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setChatListInitial } from '../../actions/chatList';
import { getChatsV2 } from '../../actions/chatList/getChatsV2';
import { resetChat } from '../../actions/chatWindow';
import { setMasterActiveTab } from '../../actions/masterFilters';
import { clearOpenedChats } from '../../actions/openedChats/actions';
import { getSubAgents } from '../../actions/subAgents';
import { MasterTab } from '../../components/MasterTab';
import { MasterTabsLayout } from '../../components/MasterTabsLayout';
import { useChangeTabTracking } from '../../hooks';
import {
  chatListSelector,
  isShowSubAgentChatListSelector,
  masterFiltersSelector,
  subAgentsSelector,
  unreadMessagesCountSelector,
} from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { EMasterTabs } from '../../types/masterFilters';
import { useApplicationContext } from '../../utils/applicationContext';
import { isTopTabsRedesignedEnabled } from '../../utils/isTopTabsRedesignedEnabled';
import { EChatListTrigger } from '../../utils/logging/publicApiDiagnostics';
import { ChangeListButtonContainer } from '../ChangeListButtonContainer';
import { ChatListContainer } from '../ChatListContainer';
import { SelectedSubAgentContainer } from '../SelectedSubAgentContainer';
import { SubAgentChatListContainer } from '../SubAgentChatListContainer';
import { SubAgentSearchContainer } from '../SubAgentSearchContainer';

const MASTER_TABS = [
  {
    title: 'Мои чаты',
    value: EMasterTabs.MasterChats,
  },
  {
    title: 'Чаты сотрудников',
    value: EMasterTabs.SubAgentsChats,
  },
];

export const MasterTabsContainer = () => {
  const { activeTab, selectedSubAgentId } = useSelector(masterFiltersSelector);
  const isShowSubAgentChatList = useSelector(isShowSubAgentChatListSelector);
  const { custom, config } = useApplicationContext();
  const { status: chatListStatus } = useSelector(chatListSelector);
  const unreadMessagesCount = useSelector(unreadMessagesCountSelector);
  const { subAgentsList, search } = useSelector(subAgentsSelector);

  const isTopTabsRedesigned = isTopTabsRedesignedEnabled(config);

  const dispatch = useDispatch();

  const isChatListLoading = chatListStatus === ERequestApiStatus.Pending;

  const handleTracking = useChangeTabTracking();

  const handleChangeTab = React.useCallback(
    (value: EMasterTabs) => {
      if (isChatListLoading || value === activeTab) {
        return;
      }

      dispatch(setMasterActiveTab({ value }));
      dispatch(setChatListInitial());
      dispatch(clearOpenedChats());
      dispatch(resetChat());
      custom.messagesMap.clear();
      handleTracking(value);

      if (value === EMasterTabs.MasterChats || (value === EMasterTabs.SubAgentsChats && selectedSubAgentId)) {
        dispatch(getChatsV2({}, EChatListTrigger.MasterTabChange));
      }

      if (value === EMasterTabs.SubAgentsChats && !selectedSubAgentId && subAgentsList.length === 0) {
        dispatch(getSubAgents({ search }));
      }
    },
    [
      activeTab,
      custom.messagesMap,
      dispatch,
      handleTracking,
      isChatListLoading,
      search,
      selectedSubAgentId,
      subAgentsList.length,
    ],
  );

  const tabsNode = React.useMemo(
    () =>
      !isTopTabsRedesigned
        ? MASTER_TABS.map(tab => (
            <MasterTab
              key={tab.value}
              title={tab.title}
              value={tab.value}
              isSelected={tab.value === activeTab}
              handleClick={handleChangeTab}
              unreadMessagesCount={tab.value === EMasterTabs.MasterChats ? unreadMessagesCount : 0}
            />
          ))
        : null,
    [activeTab, handleChangeTab, isTopTabsRedesigned, unreadMessagesCount],
  );

  const chipsNode = React.useMemo(() => {
    if (!isTopTabsRedesigned) {
      return null;
    }
    const options = MASTER_TABS.map(tab => ({
      dataTestId: `MasterTab-Chip-${tab.value}`,
      label: tab.title,
      value: tab.value,
      isSelected: tab.value === activeTab,
      counter: tab.value === EMasterTabs.MasterChats && unreadMessagesCount > 0 ? unreadMessagesCount : undefined,
    }));

    return <Chips options={options} value={[activeTab]} onChipClick={value => handleChangeTab(value)} size="S" />;
  }, [activeTab, handleChangeTab, isTopTabsRedesigned, unreadMessagesCount]);

  const layoutProps = React.useMemo(
    () => ({
      tabs: isTopTabsRedesigned ? chipsNode : tabsNode,
      selectedSubAgent: <SelectedSubAgentContainer />,
      masterTab: <ChatListContainer />,
      subAgentsTab: isShowSubAgentChatList ? <SubAgentChatListContainer /> : <SubAgentSearchContainer />,
      isMasterTabActive: activeTab === EMasterTabs.MasterChats,
      isTopTabsRedesigned,
      changeListButtonNode: isTopTabsRedesigned ? <ChangeListButtonContainer /> : null,
    }),
    [isTopTabsRedesigned, chipsNode, tabsNode, isShowSubAgentChatList, activeTab],
  );

  return <MasterTabsLayout {...layoutProps} />;
};
